/*
 * Monfiora — quick-add scent-switch fix (additive; does not modify theme core).
 *
 * Bug: in the quick-add modal, switching to a non-default scent can leave the
 * theme's variant handler unable to resolve the variant from the re-rendered
 * section, so it calls setUnavailable() and hides/disables the Add-to-cart
 * button even though the variant is in stock.
 *
 * Fix: independently resolve the selected variant from the product's own JSON
 * (/products/{handle}.js) and force the correct variant id + button state
 * (enabled, or a clear "Sold out" when genuinely unavailable). A button
 * observer re-applies if the core handler breaks it again after our pass.
 * Scoped to <quick-add-modal> only — product pages are untouched.
 */
(function () {
  if (!window.fetch) return;
  // Shared with lp-quickadd-prefetch.js so a hover/touch prefetch warms this cache.
  var cache = window.__lpqaCache = window.__lpqaCache || {};

  function handleFromUrl(u) {
    var m = (u || '').match(/\/products\/([a-z0-9][a-z0-9\-]*)/i);
    return m ? m[1] : null;
  }

  function getProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (cache[handle]) return cache[handle];
    cache[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return cache[handle];
  }

  function productUrl(modal) {
    var pi = modal.querySelector('product-info');
    var u = pi && (pi.dataset.url || pi.getAttribute('data-url'));
    if (u) return u;
    var a = modal.querySelector('a[href*="/products/"]');
    return a ? a.getAttribute('href') : null;
  }

  // Selected option values, in option (DOM) order.
  function selectedValues(modal) {
    var vs = modal.querySelector('variant-selects');
    if (!vs) return [];
    var out = [];
    vs.querySelectorAll('fieldset.product-form__input, fieldset').forEach(function (fs) {
      var c = fs.querySelector('input[type="radio"]:checked');
      if (c) out.push((c.value || '').trim());
    });
    vs.querySelectorAll('select').forEach(function (sel) {
      out.push((sel.value || '').trim());
    });
    return out.filter(function (v) { return v !== ''; });
  }

  function matchVariant(product, values) {
    if (!product || !product.variants || !values.length) return null;
    // Positional match (correct for single-option products like Monfiora's "Fragrance").
    var positional = product.variants.find(function (v) {
      var opts = v.options || [];
      if (opts.length !== values.length) return false;
      return opts.every(function (o, i) { return (o || '').trim() === values[i]; });
    });
    if (positional) return positional;
    // Order-independent fallback: guards multi-option products where the DOM/option
    // order doesn't line up with variant.options order.
    var want = values.slice().sort().join('\u0000');
    return product.variants.find(function (v) {
      var opts = (v.options || []).map(function (o) { return (o || '').trim(); });
      if (opts.length !== values.length) return false;
      return opts.slice().sort().join('\u0000') === want;
    }) || null;
  }

  function strings() { return window.variantStrings || {}; }

  function applyFix(modal) {
    var url = productUrl(modal) || modal.getAttribute('data-product-url');
    var handle = handleFromUrl(url);
    if (!handle) { if (window.console) console.warn('[lp-quickadd-fix] no product handle for modal', modal); return; }
    getProduct(handle).then(function (product) {
      if (!product) return;
      var v = matchVariant(product, selectedValues(modal));
      if (!v) return; // can't resolve — don't fight the theme
      var form = modal.querySelector('product-form');
      if (!form) return;
      var idInput = form.querySelector('input[name="id"]');
      var btn = form.querySelector('.product-form__submit, [name="add"], button[type="submit"]');
      if (!idInput || !btn) return;

      btn.__lpqaFixing = true;
      idInput.value = v.id;
      btn.classList.remove('hidden');
      var label = btn.querySelector('span');
      if (v.available) {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
        if (label && strings().addToCart) label.textContent = strings().addToCart;
      } else {
        btn.setAttribute('disabled', 'disabled');
        if (label) label.textContent = strings().soldOut || 'Sold out';
      }
      setTimeout(function () { btn.__lpqaFixing = false; }, 0);
    });
  }

  // Re-apply if the core handler disables/hides the button after our pass.
  function watchButton(modal) {
    var form = modal.querySelector('product-form');
    var btn = form && form.querySelector('.product-form__submit, [name="add"], button[type="submit"]');
    if (!btn || btn.__lpqaWatched || !('MutationObserver' in window)) return;
    btn.__lpqaWatched = true;
    new MutationObserver(function () {
      if (btn.__lpqaFixing) return;
      var broken = btn.hasAttribute('disabled') || btn.classList.contains('hidden');
      if (broken) applyFix(modal);
    }).observe(btn, { attributes: true, attributeFilter: ['disabled', 'class'] });
  }

  var timers = new WeakMap();
  function schedule(modal, delay) {
    clearTimeout(timers.get(modal));
    timers.set(modal, setTimeout(function () { applyFix(modal); watchButton(modal); }, delay || 300));
  }

  // Scent/variant changed inside a quick-add modal.
  document.addEventListener('change', function (e) {
    var modal = e.target.closest && e.target.closest('quick-add-modal');
    if (!modal || !e.target.closest('variant-selects')) return;
    schedule(modal, 300);
  });

  // Modal opened — attach observer + ensure initial button is correct once content loads.
  if ('MutationObserver' in window) {
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        var t = m.target;
        if (t && t.tagName === 'QUICK-ADD-MODAL' && t.hasAttribute('open')) schedule(t, 500);
      });
    }).observe(document.documentElement, { attributes: true, attributeFilter: ['open'], subtree: true });
  }
})();
